import type { MusicAnalyser } from '../audio/MusicAnalyser.js';
import { accuracyOf, type JudgeSettings, type SimulationState } from '../sim/index.js';
import type { SimulationEvent } from '../sim/index.js';
import type { CompiledChart } from '../songpack/index.js';
import type { VisualSignalBus } from '../visual/index.js';

/**
 * Translates a run into signals, and is the only thing that knows both sides.
 *
 * This is where the arrow in the architecture actually points. `sim/` has no
 * idea the visual engine exists; the visual engine has no idea a `Simulation`
 * does. This adapter reads one and writes the other, and it lives in `runtime/`
 * because the composition root is the correct place for a thing that must know
 * both. Move it into either layer and the layering is gone.
 *
 * It is also the natural seam for everything the spec wants to plug in later. A
 * MIDI keyboard, a replay file or an idle attract loop produce these same
 * signals through their own source; the renderers cannot tell which one is
 * driving them, because the vocabulary is all they ever see.
 */

/**
 * Beats one frame may announce at once.
 *
 * Thirty-two is about eleven seconds at 175 BPM — far longer than any frame the
 * renderer can produce, and short enough that a tab restored after ten minutes
 * does not fire two thousand signals in one tick.
 */
const MAX_BEAT_CATCH_UP = 32;

export interface VisualSignalSourceOptions {
  readonly bus: VisualSignalBus;
  readonly judge: JudgeSettings;
  /** Bars per section. Eight is the usual pop-music phrase length. */
  readonly barsPerSection?: number;
  readonly beatsPerBar?: number;
}

export class VisualSignalSource {
  private readonly bus: VisualSignalBus;
  private readonly judge: JudgeSettings;
  private readonly beatsPerBar: number;
  private readonly barsPerSection: number;

  private chart: CompiledChart | null = null;
  private analyser: MusicAnalyser | null = null;

  /** Index of the last beat announced, so each fires exactly once. */
  private lastBeatIndex = -1;
  private beatTimes: readonly number[] = [];
  private bpm = 0;

  constructor(options: VisualSignalSourceOptions) {
    this.bus = options.bus;
    this.judge = options.judge;
    this.beatsPerBar = options.beatsPerBar ?? 4;
    this.barsPerSection = options.barsPerSection ?? 8;
  }

  setAnalyser(analyser: MusicAnalyser | null): void {
    this.analyser = analyser;
  }

  /**
   * Adopts a chart's beat grid.
   *
   * Prefers `detectedBeatTimes` — the grid the Python engine actually found in
   * the audio — and falls back to synthesising one from BPM only when the pack
   * carries none. A synthesised grid drifts against a song that is not perfectly
   * metronomic, which is exactly the sort of thing that reads as "the visuals
   * are slightly off" without anyone being able to say why.
   */
  setChart(chart: CompiledChart | null): void {
    this.chart = chart;
    this.lastBeatIndex = -1;
    if (chart === null) {
      this.beatTimes = [];
      this.bpm = 0;
      return;
    }
    this.bpm = chart.bpm > 0 ? chart.bpm : chart.detectedBpm;
    this.beatTimes =
      chart.detectedBeatTimes.length > 0 ? Array.from(chart.detectedBeatTimes) : [];
    this.bus.setLevel('BPM', this.bpm);
  }

  /** A new run on the same song. */
  reset(): void {
    this.lastBeatIndex = -1;
    this.analyser?.reset();
    this.bus.reset();
    this.bus.setLevel('BPM', this.bpm);
  }

  /**
   * One frame of levels, plus any beats that fell inside it.
   *
   * Called from the runtime's simulation step, which already runs once per frame
   * against `MusicClock`. Deriving the beat here rather than from wall time is
   * what keeps the visuals locked to the music through a frame drop.
   */
  sample(songTime: number, state: SimulationState): void {
    this.bus.setLevels({
      COMBO: state.combo,
      SCORE: state.score,
      ACCURACY: accuracyOf(state.score, state.totalEvents, this.judge),
    });
    this.sampleSong(songTime);
  }

  /**
   * Everything a frame can know from the song alone.
   *
   * Split out because a replay needs exactly this half and none of the other.
   * The beat grid and the frequency bands are properties of the *music*, not of
   * the performance — the same song produces the same beats and the same bass
   * whoever is playing it, or nobody. Storing them in a replay file would be
   * storing a copy of the song, and re-deriving them is both smaller and more
   * honest: a replay reproduces what the player did, against a song that plays
   * itself the same way every time.
   */
  sampleSong(songTime: number): void {
    this.bus.setLevels({ TIME: songTime, BPM: this.bpm });

    const levels = this.analyser?.sample();
    if (levels !== undefined) {
      this.bus.setLevels({
        AMPLITUDE: levels.amplitude,
        BASS: levels.bass,
        MID: levels.mid,
        TREBLE: levels.treble,
      });
      if (levels.onset > 0) {
        this.bus.emit({ name: 'ONSET', time: songTime, strength: levels.onset });
      }
    }

    this.emitBeats(songTime);
  }

  private emitBeats(songTime: number): void {
    const index = this.beatIndexAt(songTime);
    if (index < 0 || index === this.lastBeatIndex) return;

    // A frame can straddle more than one beat at high tempo or after a stall;
    // announcing only the newest would silently drop beats from the timeline.
    //
    // The catch-up used to be capped at one bar, which was too tight: a frame
    // longer than a bar — routine when several text layers repaint at 175 BPM —
    // dropped every beat but the last four, and a replay measured sixteen fewer
    // `BEAT` signals than the run it was reproducing. The cap exists only to
    // bound the pathological case (a restored tab, minutes of clock in one
    // frame), so it belongs well above any plausible frame time.
    const from = this.lastBeatIndex + 1;
    this.lastBeatIndex = index;
    for (let beat = Math.max(from, index - MAX_BEAT_CATCH_UP); beat <= index; beat += 1) {
      const time = this.beatTimeAt(beat);
      this.bus.emit({ name: 'BEAT', time, strength: 1, detail: { index: beat } });
      if (beat % this.beatsPerBar === 0) {
        const bar = beat / this.beatsPerBar;
        this.bus.emit({ name: 'BAR', time, strength: 1, detail: { index: bar } });
        if (bar % this.barsPerSection === 0) {
          this.bus.emit({
            name: 'SECTION',
            time,
            strength: 1,
            detail: { index: bar / this.barsPerSection },
          });
        }
      }
    }
  }

  private beatIndexAt(songTime: number): number {
    if (this.beatTimes.length > 0) {
      // Linear from the last known beat: the grid only moves forwards during a
      // run, so a scan from where we were is O(1) in practice.
      let index = this.lastBeatIndex;
      while (index + 1 < this.beatTimes.length && (this.beatTimes[index + 1] as number) <= songTime) {
        index += 1;
      }
      return index;
    }
    if (this.bpm <= 0) return -1;
    const offset = this.chart?.firstBeatOffsetSeconds ?? 0;
    const elapsed = songTime - offset;
    return elapsed < 0 ? -1 : Math.floor((elapsed * this.bpm) / 60);
  }

  private beatTimeAt(index: number): number {
    if (index < 0) return 0;
    const detected = this.beatTimes[index];
    if (detected !== undefined) return detected;
    if (this.bpm <= 0) return 0;
    return (this.chart?.firstBeatOffsetSeconds ?? 0) + (index * 60) / this.bpm;
  }

  /**
   * Publishes what the simulation just decided.
   *
   * Takes the events the runtime already drains rather than subscribing to the
   * simulation itself, so this cannot change how many times that queue is read.
   */
  publish(events: readonly SimulationEvent[]): void {
    for (const event of events) {
      if (event.type !== 'waveResolved') continue;
      const missed = event.grade === 'miss';
      this.bus.emit({
        name: missed ? 'NOTE_MISS' : 'NOTE_HIT',
        time: event.songTime,
        // A late hit is a weaker signal than an exact one, so the visuals lean
        // on accuracy without the renderer having to know the grade table.
        strength: missed ? 1 : gradeStrength(event.grade),
        detail: {
          grade: event.grade,
          offsetMs: event.timingOffsetMs,
          direction: event.timingDirection,
          combo: event.combo,
          tapped: event.tapped,
          originX: event.originX,
          originY: event.originY,
          badAttempts: event.badAttempts,
        },
      });
    }
  }

  /**
   * A press, whether or not it hit anything.
   *
   * Emitted for every tap, because in `ART` mode a press that judged nothing is
   * still the player touching the artwork and is worth as much as one that did.
   */
  publishInput(
    songTime: number,
    worldX: number,
    worldY: number,
    judged: boolean,
    beatDistance = 0,
  ): void {
    this.bus.emit({
      name: 'PLAYER_INPUT',
      time: songTime,
      // An off-beat press is the *stronger* signal, not the weaker one: it is
      // the deliberate intervention, and ART mode leans on it.
      strength: judged ? 0.7 : 1,
      detail: {
        x: worldX,
        y: worldY,
        judged,
        beatDistance,
        // Named so a renderer can branch without re-deriving the rule.
        inputType: judged ? 'note' : 'offBeat',
        inputTime: songTime,
      },
    });
  }

  /**
   * How far a moment is from the nearest beat, in seconds.
   *
   * Measured against the same grid the visuals run on, so "off-beat" means off
   * the grid the picture is using rather than off the chart's note times. A tap
   * between two notes that lands exactly on a beat is not off-beat.
   */
  distanceToBeat(songTime: number): number {
    if (this.beatTimes.length > 0) {
      let best = Number.POSITIVE_INFINITY;
      // The grid is ordered, and the last index is close to now, so this walks
      // a couple of entries rather than the whole song.
      const start = Math.max(0, this.lastBeatIndex - 1);
      for (let index = start; index < this.beatTimes.length; index += 1) {
        const distance = Math.abs((this.beatTimes[index] as number) - songTime);
        if (distance < best) best = distance;
        else break;
      }
      return Number.isFinite(best) ? best : 0;
    }
    if (this.bpm <= 0) return 0;
    const period = 60 / this.bpm;
    const offset = this.chart?.firstBeatOffsetSeconds ?? 0;
    const phase = ((songTime - offset) % period + period) % period;
    return Math.min(phase, period - phase);
  }
}

function gradeStrength(grade: string): number {
  switch (grade) {
    case 'perfect':
      return 1;
    case 'great':
      return 0.8;
    case 'good':
      return 0.6;
    case 'bad':
      return 0.4;
    default:
      return 0.5;
  }
}
